<template>
  <div class="audio-player">
    <div
      class="audio-cover-wrap"
      :class="{ 'is-visualizing': visualizerActive }"
    >
      <img class="audio-cover" :src="coverUrl" alt="" />
      <div class="audio-cover-template" aria-hidden="true">
        <div class="audio-visual-core">
          <div class="audio-pulse-ring"></div>
          <div class="audio-wave-bars">
            <span
              v-for="bar in 15"
              :key="bar"
              :style="{ animationDelay: `${bar * -0.08}s` }"
            ></span>
          </div>
        </div>
      </div>
    </div>
    <div class="audio-controls">
      <div class="audio-progress-row">
        <span class="audio-time">{{ formatTime(currentTime) }}</span>
        <div class="audio-progress-wrap">
          <input
            type="range"
            class="audio-progress"
            min="0"
            :max="duration || 0"
            :value="currentTime"
            :style="progressTrackStyle"
            step="0.1"
            @input="onProgressInput"
            @mousedown="dragging = true"
            @mouseup="onProgressCommit"
            @touchstart="dragging = true"
            @touchend="onProgressCommit"
          />
        </div>
        <span class="audio-time">{{ formatTime(duration) }}</span>
      </div>
      <div class="audio-main-controls">
        <div class="audio-side audio-side--rate">
          <button class="audio-rate-btn" @click="cycleRate">
            {{ playbackRate }}x
          </button>
        </div>
        <button class="audio-skip-btn" @click="skip(-15)">
          <svg viewBox="0 0 36 36" width="36" height="36">
            <path
              d="M18 8V3l-7 5 7 5V8c5 0 9 4 9 9s-4 9-9 9-9-4-9-9H7c0 6.1 4.9 11 11 11s11-4.9 11-11S24.1 6 18 6z"
              fill="currentColor"
            />
            <text
              x="18"
              y="21"
              text-anchor="middle"
              font-size="9"
              font-weight="700"
              fill="currentColor"
            >
              15
            </text>
          </svg>
        </button>
        <button class="audio-play-main" @click="togglePlay">
          <span v-if="!playing" class="play-icon-main">&#9654;</span>
          <span v-else class="pause-icon-main">&#10074;&#10074;</span>
        </button>
        <button class="audio-skip-btn" @click="skip(30)">
          <svg viewBox="0 0 36 36" width="36" height="36">
            <path
              d="M18 8V3l7 5-7 5V8c-5 0-9 4-9 9s4 9 9 9 9-4 9-9h2c0 6.1-4.9 11-11 11S7 24.1 7 18 11.9 6 18 6z"
              fill="currentColor"
            />
            <text
              x="18"
              y="21"
              text-anchor="middle"
              font-size="9"
              font-weight="700"
              fill="currentColor"
            >
              30
            </text>
          </svg>
        </button>
        <div class="audio-side audio-side--volume">
          <div class="audio-volume">
            <button class="audio-mute-btn" @click="toggleMute">
              <svg
                v-if="!muted && volume > 0"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
                <path
                  d="M16 8.5c1 1 1.5 2.2 1.5 3.5S17 14.5 16 15.5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
                <path
                  d="M18.5 6c1.6 1.6 2.5 3.6 2.5 6s-.9 4.4-2.5 6"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
              <svg v-else viewBox="0 0 24 24" width="20" height="20">
                <path d="M4 9v6h4l5 4V5L8 9H4z" fill="currentColor" />
                <path
                  d="M17 9l4 4m0-4l-4 4"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </button>
            <input
              type="range"
              class="audio-volume-slider"
              min="0"
              max="1"
              step="0.01"
              :value="muted ? 0 : volume"
              @input="onVolumeInput"
            />
          </div>
        </div>
      </div>
    </div>
    <audio
      ref="audio"
      :src="audioUrl"
      preload="metadata"
      @loadedmetadata="onLoadedMetadata"
      @timeupdate="onTimeUpdate"
      @play="onPlay"
      @pause="onPause"
      @ended="onEnded"
      @error="onError"
    ></audio>
    <div v-if="errorMsg" class="audio-error">{{ errorMsg }}</div>
  </div>
</template>

<script>
import { playbackKeepAlive } from "@saas/service-api/src/learning-center";
import {
  getSharedPlayerLastVolume,
  getSharedPlayerMuted,
  getSharedPlayerRawVolume,
  setSharedPlayerSettings,
} from "../utils/player-volume";

const RATES = [0.75, 1, 1.25, 1.5, 2];
const HEARTBEAT_INTERVAL = 60000;

export default {
  props: {
    lessonInfo: { type: Object, default: () => ({}) },
    courseInfo: { type: Object, default: () => ({}) },
    startTime: { type: Number, default: 0 },
    trialDuration: { type: Number, default: 0 },
  },
  data() {
    return {
      playing: false,
      currentTime: 0,
      duration: 0,
      playbackRate: 1,
      dragging: false,
      trialExpired: false,
      heartbeatTimer: null,
      errorMsg: "",
      volume: getSharedPlayerRawVolume(),
      muted: getSharedPlayerMuted(),
      lastVolume: getSharedPlayerLastVolume(),
      lastHeartbeatAt: 0,
      pendingAutoplayHandler: null,
    };
  },
  computed: {
    audioUrl() {
      return this.lessonInfo.video_url || "";
    },
    coverUrl() {
      return this.lessonInfo.cover_url || this.courseInfo.cover_url || "";
    },
    visualizerActive() {
      return this.playing;
    },
    progressPercent() {
      if (!this.duration) return 0;
      const percent = (this.currentTime / this.duration) * 100;
      return Math.max(0, Math.min(100, percent));
    },
    progressTrackStyle() {
      return {
        background: `linear-gradient(90deg, #00c89d 0%, #00c89d ${this.progressPercent}%, var(--mg-control-hover-bg) ${this.progressPercent}%, var(--mg-control-hover-bg) 100%)`,
      };
    },
  },
  watch: {
    audioUrl() {
      this.errorMsg = "";
    },
  },
  mounted() {
    this.heartbeatTimer = setInterval(() => {
      if (this.playing) this.sendHeartbeat();
    }, HEARTBEAT_INTERVAL);
    // 切换课时后自动播放
    const audio = this.$refs.audio;
    if (audio) {
      this.pendingAutoplayHandler = () => {
        if (!this.playing && !this.trialExpired) {
          audio.play().catch(() => undefined);
        }
        this.pendingAutoplayHandler = null;
      };
      audio.addEventListener("canplay", this.pendingAutoplayHandler, {
        once: true,
      });
    }
  },
  methods: {
    syncSharedAudioSettings() {
      setSharedPlayerSettings({
        volume: this.volume,
        muted: this.muted,
        lastVolume: this.lastVolume,
      });
    },
    applyAudioSettings() {
      const audio = this.$refs.audio;
      if (!audio) return;
      audio.volume = this.volume;
      audio.muted = this.muted;
      audio.playbackRate = this.playbackRate;
    },
    resolveStartTime() {
      const startTime = Number(this.startTime) || 0;
      if (startTime <= 0) return 0;
      if (this.duration && startTime >= this.duration) return 0;
      return startTime;
    },
    togglePlay() {
      const audio = this.$refs.audio;
      if (!audio) return;
      if (this.trialExpired) {
        this.$emit("trial-expired");
        return;
      }
      if (this.playing) {
        this.cancelPendingAutoplay();
        audio.pause();
      } else {
        if (
          audio.ended ||
          (this.duration > 0 && audio.currentTime >= this.duration)
        ) {
          audio.currentTime = 0;
          this.currentTime = 0;
        }
        audio.play().catch(() => undefined);
      }
    },
    skip(seconds) {
      const audio = this.$refs.audio;
      if (!audio) return;
      if (this.trialExpired) {
        this.$emit("trial-expired");
        return;
      }
      let target = audio.currentTime + seconds;
      target = Math.max(0, Math.min(target, this.duration));
      if (this.trialDuration > 0 && target >= this.trialDuration) {
        target = this.trialDuration;
      }
      audio.currentTime = target;
      this.currentTime = target;
    },
    onLoadedMetadata() {
      const audio = this.$refs.audio;
      if (audio) {
        this.duration = Number.isFinite(audio.duration) ? audio.duration : 0;
        this.$emit("videoDurationChange", this.duration);
        this.applyAudioSettings();
        const startTime = this.resolveStartTime();
        audio.currentTime = startTime;
        this.currentTime = startTime;
      }
      this.setupMediaSession();
    },
    setupMediaSession() {
      if (!("mediaSession" in navigator)) return;
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: this.lessonInfo.lesson_name || "",
        artist: this.courseInfo.course_name || "",
        artwork: this.coverUrl
          ? [{ src: this.coverUrl, sizes: "512x512", type: "image/png" }]
          : [],
      });
      navigator.mediaSession.setActionHandler("play", () => this.togglePlay());
      navigator.mediaSession.setActionHandler("pause", () => this.togglePlay());
      navigator.mediaSession.setActionHandler("seekbackward", () =>
        this.skip(-15)
      );
      navigator.mediaSession.setActionHandler("seekforward", () =>
        this.skip(30)
      );
    },
    cleanupMediaSession() {
      if (!("mediaSession" in navigator)) return;
      ["play", "pause", "seekbackward", "seekforward"].forEach((action) => {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch {
          return;
        }
      });
      try {
        navigator.mediaSession.metadata = null;
      } catch {
        return;
      }
    },
    cancelPendingAutoplay() {
      const audio = this.$refs.audio;
      if (!audio || !this.pendingAutoplayHandler) return;
      audio.removeEventListener("canplay", this.pendingAutoplayHandler);
      this.pendingAutoplayHandler = null;
    },
    cleanupAudioElement() {
      const audio = this.$refs.audio;
      if (!audio) return;
      this.cancelPendingAutoplay();
      audio.pause();
      audio.removeAttribute("src");
      audio.load();
    },
    onPlay() {
      this.playing = true;
    },
    onPause() {
      this.playing = false;
      this.sendHeartbeat({ force: true });
    },
    onTimeUpdate() {
      if (this.dragging) return;
      const audio = this.$refs.audio;
      if (!audio) return;
      this.currentTime = audio.currentTime;
      if (
        this.trialDuration > 0 &&
        audio.currentTime >= this.trialDuration &&
        !this.trialExpired
      ) {
        this.trialExpired = true;
        audio.pause();
        this.$emit("trial-expired");
        return;
      }
      this.sendHeartbeatIfNeeded();
      this.$emit("timeupdate", {
        currentTime: audio.currentTime,
        duration: audio.duration,
      });
    },
    onEnded() {
      const audio = this.$refs.audio;
      this.playing = false;
      this.sendHeartbeat({ force: true });
      if (audio) {
        audio.currentTime = 0;
      }
      this.currentTime = 0;
    },
    onProgressInput(e) {
      this.currentTime = parseFloat(e.target.value) || 0;
    },
    onProgressCommit() {
      this.dragging = false;
      const audio = this.$refs.audio;
      if (!audio) return;
      let target = this.currentTime;
      if (this.trialDuration > 0 && target >= this.trialDuration) {
        target = this.trialDuration;
        this.trialExpired = true;
        this.$emit("trial-expired");
      }
      audio.currentTime = target;
      this.currentTime = target;
      this.sendHeartbeat({ force: true });
    },
    playToSeekTime(time) {
      const audio = this.$refs.audio;
      if (!audio) return;
      if (this.trialExpired) {
        this.$emit("trial-expired");
        return;
      }
      const duration = Number.isFinite(audio.duration)
        ? audio.duration
        : this.duration;
      let target = Math.max(0, Number(time) || 0);
      if (duration > 0) {
        target = Math.min(target, duration);
      }
      if (this.trialDuration > 0 && target >= this.trialDuration) {
        target = this.trialDuration;
        this.trialExpired = true;
        this.$emit("trial-expired");
      }
      audio.currentTime = target;
      this.currentTime = target;
      this.sendHeartbeat({ force: true });
      audio.play().catch(() => undefined);
    },
    cycleRate() {
      const idx = RATES.indexOf(this.playbackRate);
      this.playbackRate = RATES[(idx + 1) % RATES.length];
      const audio = this.$refs.audio;
      if (audio) {
        audio.playbackRate = this.playbackRate;
      }
    },
    onVolumeInput(e) {
      const value = Math.max(0, Math.min(1, parseFloat(e.target.value) || 0));
      this.volume = value;
      if (value > 0) {
        this.lastVolume = value;
        this.muted = false;
      } else {
        this.muted = true;
      }
      this.syncSharedAudioSettings();
      this.applyAudioSettings();
    },
    toggleMute() {
      if (this.muted || this.volume === 0) {
        this.volume = this.lastVolume > 0 ? this.lastVolume : 1;
        this.muted = false;
      } else {
        this.lastVolume = this.volume;
        this.muted = true;
      }
      this.syncSharedAudioSettings();
      this.applyAudioSettings();
    },
    sendHeartbeatIfNeeded() {
      this.sendHeartbeat();
    },
    sendHeartbeat({ force = false } = {}) {
      if (!this.courseInfo.id || !this.lessonInfo.id) return;
      const progressTime = Math.floor(this.currentTime);
      if (progressTime <= 0) return;
      const now = Date.now();
      if (!force && now - this.lastHeartbeatAt < HEARTBEAT_INTERVAL) return;
      this.lastHeartbeatAt = now;
      playbackKeepAlive({
        course_id: this.courseInfo.id,
        lesson_id: this.lessonInfo.id,
        client_type: 1,
        play_time: new Date().getTime(),
        video_url: this.audioUrl,
        video_progress_time: progressTime,
        video_speed: this.playbackRate,
        video_index: 1,
      }).catch(() => undefined);
    },
    onError() {
      this.errorMsg = "音频加载失败，请检查网络或稍后重试";
      this.playing = false;
    },
    formatTime(sec) {
      if (!sec || isNaN(sec)) return "00:00";
      const h = Math.floor(sec / 3600);
      const m = Math.floor((sec % 3600) / 60);
      const s = Math.floor(sec % 60);
      if (h > 0) {
        return (
          h +
          ":" +
          String(m).padStart(2, "0") +
          ":" +
          String(s).padStart(2, "0")
        );
      }
      return String(m).padStart(2, "0") + ":" + String(s).padStart(2, "0");
    },
  },
  beforeDestroy() {
    clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
    this.sendHeartbeat({ force: true });
    this.cleanupMediaSession();
    this.cleanupAudioElement();
  },
};
</script>

<style lang="scss" scoped>
.audio-player {
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: var(--mg-bg-surface);
  border-radius: 12px;
  overflow: hidden;
}

.audio-cover-wrap {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: radial-gradient(
    circle at 50% 40%,
    var(--mg-control-hover-bg) 0%,
    var(--mg-bg-surface) 48%,
    var(--mg-bg-page) 100%
  );
  overflow: hidden;
}

.audio-cover {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: saturate(0.92) brightness(0.78);
  transform: scale(1.01);
}

.audio-cover-template {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
      180deg,
      rgba(15, 23, 42, 0.12),
      rgba(15, 23, 42, 0.56)
    ),
    linear-gradient(
      90deg,
      rgba(0, 200, 157, 0.08),
      rgba(0, 200, 157, 0.16),
      rgba(0, 200, 157, 0.08)
    );
  pointer-events: none;
}

.audio-visual-core {
  position: relative;
  z-index: 1;
  width: 168px;
  height: 168px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.audio-pulse-ring {
  position: absolute;
  left: 18px;
  right: 18px;
  top: 50%;
  height: 1px;
  transform: translateY(-50%);
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(226, 232, 240, 0.42),
    transparent
  );
  opacity: 0.72;
  animation: audioRingPulse 2.8s ease-in-out infinite;
  animation-play-state: paused;
}

.audio-wave-bars {
  position: relative;
  z-index: 1;
  height: 58px;
  display: flex;
  align-items: center;
  gap: 5px;
}

.audio-wave-bars span {
  display: block;
  width: 5px;
  height: 18px;
  border-radius: 999px;
  background: linear-gradient(180deg, #f8fafc 0%, #00eb81 52%, #00c89d 100%);
  box-shadow: 0 0 16px rgba(0, 200, 157, 0.34);
  transform-origin: center;
  animation: audioWaveBounce 1.08s ease-in-out infinite;
  animation-play-state: paused;
}

.audio-wave-bars span:nth-child(3n) {
  height: 28px;
}

.audio-wave-bars span:nth-child(4n) {
  height: 36px;
}

.audio-wave-bars span:nth-child(5n) {
  height: 24px;
}

.is-visualizing {
  .audio-pulse-ring,
  .audio-wave-bars span {
    animation-play-state: running;
  }
}

@keyframes audioWaveBounce {
  0%,
  100% {
    transform: scaleY(0.48);
    opacity: 0.72;
  }
  42% {
    transform: scaleY(1.34);
    opacity: 1;
  }
  68% {
    transform: scaleY(0.82);
    opacity: 0.86;
  }
}

@keyframes audioRingPulse {
  0%,
  100% {
    transform: translateY(-50%) scaleX(0.82);
    opacity: 0.42;
  }
  50% {
    transform: translateY(-50%) scaleX(1);
    opacity: 0.78;
  }
}

.audio-controls {
  flex: 0 0 auto;
  padding: 16px 20px;
  background: var(--mg-bg-surface);
}

.audio-progress-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.audio-time {
  font-size: 12px;
  color: var(--mg-text-muted);
  font-variant-numeric: tabular-nums;
  min-width: 56px;
  text-align: center;
}

.audio-progress-wrap {
  flex: 1;
  display: flex;
  align-items: center;
}

.audio-progress {
  -webkit-appearance: none;
  appearance: none;
  display: block;
  width: 100%;
  height: 4px;
  background: var(--mg-control-hover-bg);
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  margin: 0;
  padding: 0;
  display: block;
  vertical-align: middle;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #00c89d;
    cursor: pointer;
    border: 2px solid #fff;
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: #00c89d;
    cursor: pointer;
    border: 2px solid #fff;
  }
}

.audio-main-controls {
  display: grid;
  grid-template-columns: 132px 44px 64px 44px 132px;
  align-items: center;
  justify-content: center;
  column-gap: 18px;
  margin-top: 16px;
}

.audio-side {
  width: 132px;
  display: flex;
  align-items: center;
}

.audio-side--rate {
  justify-content: flex-end;
}

.audio-side--volume {
  justify-content: flex-start;
}

.audio-skip-btn {
  width: 44px;
  height: 44px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--mg-text-secondary);
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: var(--mg-text-primary);
  }

  svg {
    width: 36px;
    height: 36px;
  }
}

.audio-play-main {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #00eb81 0%, #00c89d 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: filter 0.2s;

  &:hover {
    filter: brightness(0.94);
  }
}

.play-icon-main {
  font-size: 22px;
  color: #fff;
  margin-left: 3px;
}

.pause-icon-main {
  font-size: 18px;
  color: #fff;
  letter-spacing: 2px;
}

.audio-rate-btn {
  width: 64px;
  height: 34px;
  background: var(--mg-control-hover-bg);
  color: #e2e8f0;
  border: none;
  border-radius: 8px;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  text-align: center;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: #475569;
  }
}

.audio-volume {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 132px;
}

.audio-mute-btn {
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--mg-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;

  &:hover {
    color: var(--mg-text-primary);
  }
}

.audio-volume-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 96px;
  height: 4px;
  background: var(--mg-control-hover-bg);
  border-radius: 2px;
  outline: none;
  cursor: pointer;
  margin: 0;
  padding: 0;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #e2e8f0;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #e2e8f0;
    cursor: pointer;
    border: none;
  }
}

@media (max-width: 640px) {
  .audio-controls {
    padding: 12px 14px;
  }

  .audio-time {
    min-width: 44px;
  }

  .audio-main-controls {
    grid-template-columns: 64px 38px 56px 38px 64px;
    column-gap: 8px;
    margin-top: 12px;
  }

  .audio-side {
    width: 64px;
  }

  .audio-rate-btn {
    width: 58px;
    height: 32px;
    font-size: 13px;
  }

  .audio-skip-btn {
    width: 38px;
    height: 38px;

    svg {
      width: 32px;
      height: 32px;
    }
  }

  .audio-play-main {
    width: 56px;
    height: 56px;
  }

  .audio-volume {
    width: 64px;
    justify-content: flex-start;
  }

  .audio-volume-slider {
    display: none;
  }
}

.audio-error {
  padding: 12px 20px;
  background: var(--mg-bg-surface);
  color: #f87171;
  font-size: 13px;
  text-align: center;
}
</style>
